import { useState, useRef } from 'react'
import { X, User, Mail, Pencil, KeyRound, LogOut, Camera, Settings, Trash2, Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useWorkspace } from '../lib/WorkspaceContext'
import MiPrivacidad from './MiPrivacidad'
import './Perfil.css'

function InputPassword({ value, onChange, placeholder, onKeyDown }) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="auth-campo-password">
      <input
        className="form-input"
        type={visible ? 'text' : 'password'}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="auth-toggle-password"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        tabIndex={-1}
      >
        {visible ? <EyeOff size={18} strokeWidth={1.8} /> : <Eye size={18} strokeWidth={1.8} />}
      </button>
    </div>
  )
}

export default function Perfil({ abierto, onCerrar, usuario, historial = [], favoritos, favoritosDetalle = [], onVerReceta, onLogout, onEliminarCuenta, onNavegar, onActualizarUsuario }) {
  const nombre = usuario?.user_metadata?.nombre || usuario?.email?.split('@')[0] || 'Paciente'
  const fotoPerfilUrl = usuario?.user_metadata?.foto_perfil_url
  const [modalEliminar, setModalEliminar] = useState(false)
  const [confirmacion, setConfirmacion] = useState('')
  const [eliminando, setEliminando] = useState(false)
  const { workspaceActivo, workspaces, elegirWorkspace, esSuperAdmin } = useWorkspace()

  const [subiendoFotoPerfil, setSubiendoFotoPerfil] = useState(false)
  const inputFotoPerfilRef = useRef(null)

  const [edicionNombre, setEdicionNombre] = useState(false)
  const [nombreInput, setNombreInput] = useState('')
  const [guardandoNombre, setGuardandoNombre] = useState(false)
  const [errorNombre, setErrorNombre] = useState('')

  const [historialAbierto, setHistorialAbierto] = useState(false)
  const [configAvanzadaAbierta, setConfigAvanzadaAbierta] = useState(false)

  const [modalPassword, setModalPassword] = useState(false)
  const [passwordActual, setPasswordActual] = useState('')
  const [passwordNueva, setPasswordNueva] = useState('')
  const [passwordConfirmar, setPasswordConfirmar] = useState('')
  const [cambiandoPassword, setCambiandoPassword] = useState(false)
  const [errorPassword, setErrorPassword] = useState('')

  const confirmarEliminacion = async () => {
    if (confirmacion.trim().toUpperCase() !== 'ELIMINAR') return
    setEliminando(true)
    await onEliminarCuenta()
    setEliminando(false)
  }

  const navegarYCerrar = (destino) => {
    onCerrar()
    onNavegar(destino)
  }

  const iniciarEdicionNombre = () => {
    setNombreInput(nombre)
    setErrorNombre('')
    setEdicionNombre(true)
  }

  const cancelarEdicionNombre = () => {
    setEdicionNombre(false)
    setErrorNombre('')
  }

  const guardarNombre = async () => {
    const nuevoNombre = nombreInput.trim()
    if (!nuevoNombre) { setErrorNombre('El nombre no puede estar vacío'); return }
    setGuardandoNombre(true); setErrorNombre('')
    const { data, error } = await supabase.auth.updateUser({ data: { nombre: nuevoNombre } })
    setGuardandoNombre(false)
    if (error) { setErrorNombre('No se pudo guardar el nombre. Intentá de nuevo.'); return }
    onActualizarUsuario(data.user)
    setEdicionNombre(false)
  }

  const subirFotoPerfil = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setSubiendoFotoPerfil(true)
    const ext = file.name.split('.').pop()
    const path = `${usuario.id}/perfil.${ext}`

    const { error: errorSubida } = await supabase.storage.from('fotos-perfil').upload(path, file, { upsert: true })
    if (errorSubida) { alert('No se pudo subir la foto. Probá de nuevo.'); setSubiendoFotoPerfil(false); return }

    const { data: { publicUrl } } = supabase.storage.from('fotos-perfil').getPublicUrl(path)
    const urlVersionada = `${publicUrl}?v=${Date.now()}`

    const { data, error } = await supabase.auth.updateUser({ data: { foto_perfil_url: urlVersionada } })
    setSubiendoFotoPerfil(false)
    if (error) { alert('La foto se subió pero no se pudo guardar. Probá de nuevo.'); return }
    onActualizarUsuario(data.user)
  }

  const cerrarModalPassword = () => {
    setModalPassword(false)
    setPasswordActual(''); setPasswordNueva(''); setPasswordConfirmar('')
    setErrorPassword('')
  }

  const cambiarPassword = async () => {
    if (passwordNueva.length < 6) { setErrorPassword('La contraseña nueva debe tener al menos 6 caracteres'); return }
    if (passwordNueva !== passwordConfirmar) { setErrorPassword('Las contraseñas nuevas no coinciden'); return }
    setCambiandoPassword(true); setErrorPassword('')

    const { error: errorActual } = await supabase.auth.signInWithPassword({ email: usuario.email, password: passwordActual })
    if (errorActual) { setErrorPassword('La contraseña actual es incorrecta'); setCambiandoPassword(false); return }

    const { error } = await supabase.auth.updateUser({ password: passwordNueva })
    setCambiandoPassword(false)
    if (error) { setErrorPassword('No se pudo actualizar la contraseña. Intentá de nuevo.'); return }
    cerrarModalPassword()
  }

  return (
    <>
      <div className={`perfil-overlay ${abierto ? 'abierto' : ''}`} onClick={onCerrar} />
      <div className={`perfil-drawer ${abierto ? 'abierto' : ''}`}>
        <button className="perfil-drawer-cerrar" onClick={onCerrar} aria-label="Cerrar">
          <X size={20} strokeWidth={2} />
        </button>

        <div className="perfil-header">
          <button
            className="perfil-avatar"
            style={fotoPerfilUrl ? { backgroundImage: `url(${fotoPerfilUrl})` } : undefined}
            onClick={() => !subiendoFotoPerfil && inputFotoPerfilRef.current?.click()}
            aria-label="Cambiar foto de perfil (opcional)"
          >
            {!fotoPerfilUrl && nombre.charAt(0).toUpperCase()}
            <span className="perfil-avatar-editar"><Camera size={12} strokeWidth={2} /></span>
          </button>
          <input ref={inputFotoPerfilRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={subirFotoPerfil} />
          <div>
            <h1 className="perfil-nombre">¡Hola {nombre}!</h1>
            <p className="perfil-proposito">Acá podés actualizar tus datos y tu cuenta.</p>
          </div>
        </div>

        <div className="perfil-contenido">

          {/* PERSONALIZÁ TU ESPACIO — hub de onboarding (#48), antes llamado "Configuración" */}
          <div className="perfil-seccion">
            <div className="perfil-menu-lista">
              <button className="perfil-menu-item perfil-menu-item-boton" onClick={() => navegarYCerrar('configuracion-paciente')}>
                <div className="perfil-menu-icono"><Settings size={18} strokeWidth={2} /></div>
                <div className="perfil-menu-texto">
                  <p className="perfil-menu-label">Personalizá tu espacio</p>
                  <p className="perfil-menu-valor">Terminá de armar tu espacio</p>
                </div>
                <span className="perfil-menu-arrow">›</span>
              </button>
            </div>
          </div>

          {/* CONFIGURACIÓN — datos personales + Eliminar cuenta escondida detrás
              de "Opciones avanzadas" (Bloque 1.5.10: no al mismo nivel que
              Cerrar sesión, ni accesible en un solo tap desde el drawer) */}
          <div className="perfil-seccion">
            <p className="seccion-titulo">⚙️ Configuración</p>
            <div className="perfil-menu-lista">
              <div className="perfil-menu-item">
                <div className="perfil-menu-icono"><User size={18} strokeWidth={2} /></div>
                {edicionNombre ? (
                  <div className="perfil-menu-edicion">
                    <input
                      className="form-input"
                      value={nombreInput}
                      onChange={e => setNombreInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && guardarNombre()}
                      autoFocus
                    />
                    {errorNombre && <p className="perfil-error">{errorNombre}</p>}
                    <div className="perfil-menu-edicion-acciones">
                      <button className="btn-secundario" onClick={cancelarEdicionNombre} disabled={guardandoNombre}>Cancelar</button>
                      <button className="btn-primario" onClick={guardarNombre} disabled={guardandoNombre}>
                        {guardandoNombre ? 'Guardando...' : 'Guardar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="perfil-menu-texto">
                      <p className="perfil-menu-label">Nombre</p>
                      <p className="perfil-menu-valor">{nombre}</p>
                    </div>
                    <button className="perfil-menu-accion" onClick={iniciarEdicionNombre} aria-label="Editar nombre">
                      <Pencil size={16} strokeWidth={2} />
                    </button>
                  </>
                )}
              </div>
              <div className="perfil-menu-item">
                <div className="perfil-menu-icono"><Mail size={18} strokeWidth={2} /></div>
                <div className="perfil-menu-texto">
                  <p className="perfil-menu-label">Email</p>
                  <p className="perfil-menu-valor">{usuario?.email}</p>
                </div>
              </div>
            </div>

            <button className="perfil-historial-toggle" style={{ marginTop: 8 }} onClick={() => setConfigAvanzadaAbierta(v => !v)}>
              Opciones avanzadas
              <span>{configAvanzadaAbierta ? '▲' : '▼'}</span>
            </button>
            {configAvanzadaAbierta && (
              <div className="perfil-menu-lista" style={{ marginTop: 8 }}>
                <button className="perfil-menu-item perfil-menu-item-boton perfil-menu-item-peligro" onClick={() => setModalEliminar(true)}>
                  <div className="perfil-menu-icono"><Trash2 size={18} strokeWidth={2} /></div>
                  <div className="perfil-menu-texto">
                    <p className="perfil-menu-label">Eliminar cuenta</p>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* WORKSPACE — si hay logo, reemplaza el título genérico (el logo
              convive con el nombre, nunca lo reemplaza a él) */}
          <div className="perfil-seccion">
            {workspaceActivo?.logo_url ? (
              <div className="perfil-workspace-header" style={{ marginBottom: workspaces.length > 1 ? 8 : 0 }}>
                <img src={workspaceActivo.logo_url} alt="" className="perfil-workspace-logo" />
                <p className="perfil-workspace-nombre">{workspaceActivo?.nombre}</p>
              </div>
            ) : (
              <>
                <p className="seccion-titulo">🌿 Tu espacio</p>
                <p style={{ fontSize: 14, marginBottom: workspaces.length > 1 ? 8 : 0 }}>{workspaceActivo?.nombre}</p>
              </>
            )}
            {workspaces.length > 1 && (
              <select
                className="form-input"
                value={workspaceActivo?.id || ''}
                onChange={e => elegirWorkspace(e.target.value)}
              >
                {workspaces.map(w => (
                  <option key={w.id} value={w.id}>{w.nombre}</option>
                ))}
              </select>
            )}
          </div>

          {/* MI PRIVACIDAD */}
          <MiPrivacidad usuario={usuario} />

          {/* SEGURIDAD */}
          <div className="perfil-seccion">
            <p className="seccion-titulo">🔐 Seguridad</p>
            <div className="perfil-menu-lista">
              <button className="perfil-menu-item perfil-menu-item-boton" onClick={() => setModalPassword(true)}>
                <div className="perfil-menu-icono"><KeyRound size={18} strokeWidth={2} /></div>
                <div className="perfil-menu-texto">
                  <p className="perfil-menu-label">Cambiar contraseña</p>
                </div>
                <span className="perfil-menu-arrow">›</span>
              </button>
            </div>
          </div>

          {/* AYUDA (incluye la bandeja de reportes para super-admin) */}
          <div className="perfil-seccion">
            <p className="seccion-titulo">💬 Ayuda</p>
            <button className="btn-secundario" style={{ width: '100%' }} onClick={() => navegarYCerrar('reportes')}>
              {esSuperAdmin
                ? '📬 Bandeja de reportes'
                : workspaceActivo?.rolEnWorkspace === 'owner'
                  ? '📋 Reportar una incidencia'
                  : '💬 Reportar un problema o sugerencia'}
            </button>
          </div>

          {/* STATS */}
          <div className="perfil-stats">
            <div className="stat-item">
              <span className="stat-valor">{favoritosDetalle.length}</span>
              <span className="stat-label">Favoritos</span>
            </div>
            <div className="stat-divider" />
            <div className="stat-item">
              <span className="stat-valor">{historial.length}</span>
              <span className="stat-label">Realizadas</span>
            </div>
          </div>

          {/* FAVORITOS */}
          {favoritosDetalle.length > 0 && (
            <div className="perfil-seccion">
              <p className="seccion-titulo">❤️ Mis favoritos</p>
              <div className="perfil-lista">
                {favoritosDetalle.map(receta => (
                  <div key={receta.id} className="perfil-item" onClick={() => { onCerrar(); onVerReceta(receta) }}>
                    <div>
                      <p className="perfil-item-nombre">{receta.nombre}</p>
                      <p className="perfil-item-fecha">⏱ {receta.tiempo_prep_min} min</p>
                    </div>
                    <span className="perfil-item-arrow">›</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* HISTORIAL — colapsado por defecto, no es un bloque destacado */}
          {historial.length > 0 && (
            <div className="perfil-seccion">
              <button className="perfil-historial-toggle" onClick={() => setHistorialAbierto(v => !v)}>
                📖 Historial de recetas ({historial.length})
                <span>{historialAbierto ? '▲' : '▼'}</span>
              </button>
              {historialAbierto && (
                <div className="perfil-lista" style={{ marginTop: 8 }}>
                  {historial.map((receta, i) => (
                    <div key={i} className="perfil-item" onClick={() => { onCerrar(); onVerReceta(receta) }}>
                      <div>
                        <p className="perfil-item-nombre">{receta.nombre}</p>
                        <p className="perfil-item-fecha">{receta.fecha}</p>
                      </div>
                      <span className="perfil-item-arrow">›</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="perfil-separador" />

          {/* CUENTA */}
          <div className="perfil-seccion">
            <p className="seccion-titulo">⚙️ Cuenta</p>
            <div className="perfil-menu-lista">
              <button className="perfil-menu-item perfil-menu-item-boton" onClick={onLogout}>
                <div className="perfil-menu-icono"><LogOut size={18} strokeWidth={2} /></div>
                <div className="perfil-menu-texto">
                  <p className="perfil-menu-label">Cerrar sesión</p>
                </div>
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* MODAL CAMBIAR CONTRASEÑA */}
      {modalPassword && (
        <div className="modal-overlay" onClick={() => !cambiandoPassword && cerrarModalPassword()}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 className="modal-titulo">Cambiar contraseña</h3>

            <label className="form-label">Contraseña actual</label>
            <InputPassword
              value={passwordActual}
              onChange={e => setPasswordActual(e.target.value)}
            />

            <label className="form-label">Contraseña nueva</label>
            <InputPassword
              placeholder="Mínimo 6 caracteres"
              value={passwordNueva}
              onChange={e => setPasswordNueva(e.target.value)}
            />

            <label className="form-label">Confirmar contraseña nueva</label>
            <InputPassword
              value={passwordConfirmar}
              onChange={e => setPasswordConfirmar(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && cambiarPassword()}
            />

            {errorPassword && <p className="perfil-error" style={{ marginTop: 8 }}>{errorPassword}</p>}

            <div className="modal-actions">
              <button className="btn-secundario" onClick={cerrarModalPassword} disabled={cambiandoPassword}>Cancelar</button>
              <button className="btn-primario" onClick={cambiarPassword} disabled={cambiandoPassword}>
                {cambiandoPassword ? 'Actualizando...' : 'Actualizar contraseña'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ELIMINAR CUENTA */}
      {modalEliminar && (
        <div className="modal-overlay" onClick={() => !eliminando && setModalEliminar(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <h3 className="modal-titulo">Eliminar cuenta</h3>
            <p style={{ fontSize: 14, color: 'var(--gris)', marginBottom: 12 }}>
              Esto borra tu cuenta y todos tus datos (favoritos, historial, diario, progreso, fotos) de forma permanente. No se puede deshacer.
            </p>
            <label className="form-label">Escribí ELIMINAR para confirmar</label>
            <input className="form-input" value={confirmacion} onChange={e => setConfirmacion(e.target.value)} />
            <div className="modal-actions">
              <button className="btn-secundario" onClick={() => setModalEliminar(false)} disabled={eliminando}>Cancelar</button>
              <button
                className="btn-primario"
                style={{ background: '#C0392B' }}
                onClick={confirmarEliminacion}
                disabled={eliminando || confirmacion.trim().toUpperCase() !== 'ELIMINAR'}
              >
                {eliminando ? 'Eliminando...' : 'Eliminar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
